import { z } from 'zod';
import { requiredFieldError, requiredNumberString, requiredString } from '../../helper';
import { EnterpriseStructure } from '../../types';
import { address } from './address';

const baseBusinessDetails = z.object({
  structure: z.nativeEnum(EnterpriseStructure),
  legalEntityName: requiredString('Legal business name').max(255, 'Legal business name must be at most 255 characters'),
  registeredAddress: address,
});

export const contactDetails = z.object({
  firstName: requiredString('First name').max(255, 'First name must be at most 255 characters'),
  middleName: z.string().trim().max(255, 'Middle name must be at most 255 characters').nullish(),
  lastName: requiredString('Last name').max(255, 'Last name must be at most 255 characters'),
  residentialAddress: address,
});

export const billingContactDetails = contactDetails;

const extendedBusinessFields = {
  legalEntityName: requiredString('Legal business name').max(255, 'Legal business name must be at most 255 characters'),
  registeredAddress: address.optional(),
  tradingName: requiredString('Trading name').max(255, 'Trading name must be at most 255 characters'),
  abn: requiredString('ABN')
    .length(11, 'ABN must be exactly 11 digits')
    .regex(/^\d+$/, 'ABN must contain only numbers'),
  businessPhone: requiredString('Business phone')
    .min(10, 'Please enter a valid Australian phone number')
    .max(10, 'Please enter a valid Australian phone number')
    .regex(/^\d+$/, 'Please enter a valid Australian phone number')
    .regex(/^0\d/, 'Please enter a valid Australian phone number'),
  businessEmail: requiredString('Business email')
    .max(255, 'Business email must be at most 255 characters')
    .email('Please enter a valid email address'),
  businessWebsite: z.string().trim().max(255, 'Business website must be at most 255 characters').optional(),
  yearsInTrading: requiredString('Years in trading'),
  numberOfEmployees: requiredString('Number of employees'),
  industryCategory: requiredString('Industry category'),
  propertyStatus: requiredString('Status of property where business resides'),
  primaryContact: contactDetails,
  billingContactSameAsPrimary: z.boolean(),
  billingContact: billingContactDetails.optional(),
};

export const companyBusinessDetails = baseBusinessDetails.extend({
  structure: z.literal(EnterpriseStructure.Company),
  principalBusinessAddress: address,
  acnNumber: requiredString(),
});

export const proprietaryLimitedCompanyBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.ProprietaryLimitedCompany),
  ...extendedBusinessFields,
  acnNumber: requiredString(),
});

export const associationBusinessDetails = baseBusinessDetails.extend({
  structure: z.enum([EnterpriseStructure.IncorporatedAssociation, EnterpriseStructure.UnincorporatedAssociation]),
  associationNumber: requiredNumberString(),
});

export const partnershipBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.Partnership),
  ...extendedBusinessFields,
});

export const trustBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.Trust),
  ...extendedBusinessFields,
  trustDeedFiles: z.array(z.any()).optional(),
  trustDeedDocumentIds: z.array(z.string()).optional(),
  trustDeedDocumentNames: z.array(z.string()).optional(),
});

export const soleTraderBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.SoleTrader),
  ...extendedBusinessFields,
});

export const otherBusinessDetails = z.object({
  structure: z.literal(EnterpriseStructure.Other),
  ...extendedBusinessFields,
});

export const businessDetails = z.discriminatedUnion(
  'structure',
  [
    companyBusinessDetails,
    proprietaryLimitedCompanyBusinessDetails,
    associationBusinessDetails,
    partnershipBusinessDetails,
    trustBusinessDetails,
    soleTraderBusinessDetails,
    otherBusinessDetails,
  ],
  {
    errorMap: () => ({
      message: requiredFieldError('Entity Structure'),
    }),
  },
);

/**
 * Reusable superRefine helper that validates Trust structures have at least
 * one trust deed file or previously uploaded document. Use inside a
 * `.superRefine()` on any schema whose `enterprise` field is the shared
 * `businessDetails` discriminated union.
 */
export const refineTrustDeedRequired = (enterprise: z.infer<typeof businessDetails>, ctx: z.RefinementCtx) => {
  if (enterprise.structure !== EnterpriseStructure.Trust) return;

  const trustEnterprise = enterprise as {
    trustDeedFiles?: unknown[];
    trustDeedDocumentIds?: string[];
  };
  const hasNewFiles = Array.isArray(trustEnterprise.trustDeedFiles) && trustEnterprise.trustDeedFiles.length > 0;
  const hasExistingDocuments =
    Array.isArray(trustEnterprise.trustDeedDocumentIds) && trustEnterprise.trustDeedDocumentIds.length > 0;

  if (!hasNewFiles && !hasExistingDocuments) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requiredFieldError('Trust deed'),
      path: ['enterprise', 'trustDeedFiles'],
    });
  }
};
